import { useState } from 'react';
import type { AccountOption } from '@/types/auth';
import { Button } from '@/components/ui/Button';

interface AccountSelectionModalProps {
  accounts: AccountOption[];
  userName: string;
  onSelect: (accountId: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function AccountSelectionModal({
  accounts,
  userName,
  onSelect,
  onCancel,
  isLoading,
}: AccountSelectionModalProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const handleConfirm = () => {
    if (selectedAccountId) {
      onSelect(selectedAccountId);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return { bg: '#FEE2E2', text: '#DC2626' };
      case 'GERENTE':
        return { bg: '#DBEAFE', text: '#2563EB' };
      case 'USUARIO':
        return { bg: '#F3F4F6', text: '#6B7280' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
        style={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: '#22212C' }}
        >
          Selecionar Conta
        </h2>
        <p
          className="text-sm mb-6"
          style={{ color: 'rgba(50, 71, 92, 0.6)' }}
        >
          Olá, <strong>{userName}</strong>. Você está vinculado a mais de uma conta. Selecione qual deseja acessar:
        </p>

        <div className="space-y-3 mb-6">
          {accounts.map((account) => {
            const isSelected = selectedAccountId === account.accountId;
            const roleColor = getRoleBadgeColor(account.role);

            return (
              <button
                key={account.accountId}
                type="button"
                onClick={() => setSelectedAccountId(account.accountId)}
                className="w-full text-left p-4 rounded-lg border-2 transition-all"
                style={{
                  borderColor: isSelected ? 'var(--wl-primary)' : '#E6E6F0',
                  backgroundColor: isSelected ? 'rgba(108, 93, 211, 0.04)' : '#FFF',
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#22212C' }}>
                      {account.accountName}
                    </p>
                  </div>
                  <span
                    className="text-xs font-medium px-2 py-1 rounded"
                    style={{
                      backgroundColor: roleColor.bg,
                      color: roleColor.text,
                    }}
                  >
                    {account.role}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50 transition-colors"
            style={{ borderColor: '#E6E6F0', color: 'var(--wl-secondary)' }}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedAccountId || isLoading}
            isLoading={isLoading}
          >
            Entrar
          </Button>
        </div>
      </div>
    </div>
  );
}
